import {Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {PageHeaderComponent} from '../../util/design/page-header/page-header.component';
import {RulesetCardComponent} from '../ruleset-card/ruleset-card.component';
import {
  CreateRulesetPromptComponent,
  RulesetCreationRequest,
} from '../create-ruleset-prompt/create-ruleset-prompt.component';
import {Ruleset, RULESET_TYPE_CONFIG, RulesetType} from '../models/rule-management.models';
import {
  MyCheckQuota,
  MyCheckUsage,
  RULESET_PACKAGE_EXTENSION,
  RulesetImportResult,
  RulesetService,
} from '../../../services/ruleset.service';
import {ChatService} from '../../chatBIC/service/chat.service';
import {ChatViewComponent} from '../../chatBIC/chat-view/chat-view.component';
import {MessageViewComponent} from '../../chatBIC/message-view/message-view.component';
import {MycheckSeriesStatusComponent} from '../../chatBIC/mycheck-series-status/mycheck-series-status.component';
import {filter, firstValueFrom, Subscription} from 'rxjs';
import {RuleUsageWarningComponent} from '../rule-usage-warning/rule-usage-warning.component';
import {StateConfigService} from '../../../services/state/state-config.service';
import {
  catalogComponentKey,
  CatalogService,
  catalogSourceKey,
  CatalogSubmission,
  latestSubmissionBySource,
  rememberLatestSubmission,
} from '../../../services/catalog.service';
import {
  CatalogSubmissionPromptComponent
} from '../../my-checks/shared/catalog-submission-prompt/catalog-submission-prompt.component';
import {PermissionService} from '../../../services/permission/permission.service';
import {RulesetImportFeedbackComponent} from '../ruleset-import-feedback/ruleset-import-feedback.component';
import {EmptyStateComponent} from '../../util/design/empty-state/empty-state.component';
import {MyCheckTabsComponent} from '../../my-checks/shared/my-check-tabs/my-check-tabs.component';
import {FavoritesPromptComponent,} from '../../my-checks/shared/favorites-prompt/favorites-prompt.component';
import {
  CreateCheckRunPromptComponent,
} from '../../my-checks/shared/create-check-run-prompt/create-check-run-prompt.component';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faArrowDownWideShort,
  faArrowUpWideShort,
  faCircleInfo,
  faClipboardList,
  faMagnifyingGlass,
  faPlus,
  faStar,
} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownTriggerDirective,
  BicInputComponent,
  BicPopupComponent,
  BicToastService,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {SkeletonCardComponent} from '../../util/skeleton-card/skeleton-card.component';
import {BicFilterComponent, BicFilterConfig} from '../../util/design/bic-filter/bic-filter.component';
import {
  ShareWithProjectsPromptComponent
} from '../../my-checks/shared/share-with-projects-prompt/share-with-projects-prompt.component';
import {serverErrorMessage} from '../../my-checks/shared/server-error-message';
import {
  ProjectOwnershipMode,
  ProjectOwnershipPromptComponent,
} from '../../my-checks/shared/project-ownership-prompt/project-ownership-prompt.component';

type RulesetSortField = 'name' | 'status' | 'updated';

interface SortFieldOption {
  field: RulesetSortField;
  label: string;
  ascLabel: string;
  descLabel: string;
}

const RULESET_SORT_FIELD_OPTIONS: SortFieldOption[] = [
  {field: 'name', label: 'Name', ascLabel: 'A–Z', descLabel: 'Z–A'},
  {field: 'status', label: 'Status', ascLabel: 'Aktiv zuerst', descLabel: 'Entwurf zuerst'},
  {field: 'updated', label: 'Zuletzt geändert', ascLabel: 'Älteste zuerst', descLabel: 'Neueste zuerst'},
];

/** Reihenfolge für die Sortierung nach Status: fertige/aktive zuerst, Entwürfe danach. */
function rulesetStatusRank(status: Ruleset['status']): number {
  const order: Record<string, number> = {ACTIVE: 0, PUBLISHED: 0, DRAFT: 1, INACTIVE: 2, ARCHIVED: 3};
  return order[status ?? 'DRAFT'] ?? 4;
}

/** Robuster Zeitwert für die Sortierung – fehlendes/ungültiges Datum landet am Ende. */
function dateValue(value: string | Date | undefined): number {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

@Component({
  selector: 'app-rule-management-overview',
  standalone: true,
  imports: [
    PageHeaderComponent,
    RulesetCardComponent,
    CreateRulesetPromptComponent,
    ChatViewComponent,
    MessageViewComponent,
    MycheckSeriesStatusComponent,
    FaIconComponent,
    BicButtonComponent,
    BicDropdownComponent,
    BicDropdownTriggerDirective,
    BicInputComponent,
    BicPopupComponent,
    SkeletonCardComponent,
    BicFilterComponent,
    RuleUsageWarningComponent,
    CatalogSubmissionPromptComponent,
    ShareWithProjectsPromptComponent,
    ProjectOwnershipPromptComponent,
    TooltipDirective,
    RulesetImportFeedbackComponent,
    EmptyStateComponent,
    MyCheckTabsComponent,
    FavoritesPromptComponent,
    CreateCheckRunPromptComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './rule-management-overview.component.html',
})
export class RuleManagementOverviewComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  /**
   * Query-Parameter, der die laufende Quickstart-Session adressiert.
   *
   * <p>Ohne ihn zeigt `/my-checks` **immer** die Übersicht. Vorher nahm die Seite jede im
   * sessionStorage gemerkte Session wieder auf – auch eine längst abgeschlossene – und der Nutzer
   * landete statt in der Liste in einem leeren Chat.
   */
  private static readonly QUICKSTART_PARAM = 'quickstart';
  stateConfigService = inject(StateConfigService);
  private rulesetService = inject(RulesetService);
  /** protected, weil die Quickstart-Topbar den Pending-Zustand anzeigt. */
  protected chatService = inject(ChatService);
  /** Regelset, für das der Katalog-Einreichungsdialog offen ist (null = geschlossen). */
  provideTarget: Ruleset | null = null;
  /** Darf aus dieser Stage in den Katalog eingereicht werden? (nur für Admins geladen) */
  catalogSubmissionAllowed = false;
  /** Letzte eigene Katalog-Einreichung je Regelset – Grundlage der Karten-Badges. */
  private catalogSubmissions = new Map<string, CatalogSubmission>();
  /** Regelset, für das der Dialog „mit Projekten teilen" offen ist (null = geschlossen). */
  projectShareRuleset: Ruleset | null = null;
  /** In dieses Projekt geteiltes Regelset, dessen Entfernen gerade bestätigt wird. */
  unshareHereRuleset: Ruleset | null = null;
  unsharingHere = false;

  get currentProjectId(): number | undefined {
    return this.stateConfigService.selectedProject?.id;
  }

  onShareRequested(ruleset: Ruleset): void {
    if (ruleset.linkedIn || ruleset.isPrebuilt) return;
    this.projectShareRuleset = ruleset;
  }

  /** Neuer Freigabe-Stand – neu laden, damit die Projektliste auf der Karte stimmt. */
  onProjectSharesSaved(): void {
    this.projectShareRuleset = null;
    this.loadRulesets();
  }

  /** Geteiltes Set, für das der Eigentümer-/Lösch-Dialog offen ist (null = geschlossen). */
  ownershipRuleset: Ruleset | null = null;
  ownershipMode: ProjectOwnershipMode = 'delete';

  onTransferOwnershipRequested(ruleset: Ruleset): void {
    if (ruleset.linkedIn || !this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_MANAGE')) return;
    this.ownershipMode = 'transfer';
    this.ownershipRuleset = ruleset;
  }

  /** Nach Übergabe oder Löschen neu laden – Eigentümer, Badges und Projektliste ändern sich. */
  onOwnershipDone(): void {
    this.ownershipRuleset = null;
    this.loadRulesets();
  }

  onUnshareHereRequested(ruleset: Ruleset): void {
    if (!ruleset.linkedIn) return;
    this.unshareHereRuleset = ruleset;
  }

  cancelUnshareHere(): void {
    if (this.unsharingHere) return;
    this.unshareHereRuleset = null;
  }

  /**
   * Entfernt ein aus einem anderen Projekt geteiltes Regelset aus diesem Projekt. Das Backend nimmt
   * es dabei auch aus allen Prüfläufen dieses Projekts heraus.
   */
  confirmUnshareHere(): void {
    const ruleset = this.unshareHereRuleset;
    const projectId = this.currentProjectId;
    if (!ruleset || !projectId || this.unsharingHere) return;
    this.unsharingHere = true;
    this.rulesetService.unshareFromProject(ruleset.id, projectId, projectId).subscribe({
      next: () => {
        this.unsharingHere = false;
        this.unshareHereRuleset = null;
        this.toastService.success('myCheck aus diesem Projekt entfernt.');
        this.loadRulesets();
      },
      error: err => {
        this.unsharingHere = false;
        this.toastService.error(serverErrorMessage(err, 'Entfernen fehlgeschlagen.'));
      },
    });
  }
  private permissionService = inject(PermissionService);

  /** „myCheck verwalten" – erstellen, duplizieren, bearbeiten (früher „Prüfregeln verwalten"). */
  get canManageRules(): boolean {
    return this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_MANAGE');
  }

  /** Prüfläufe verwalten – eigenes Recht, unabhängig von den Prüfregeln. */
  get canManageMyChecks(): boolean {
    return this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_MANAGE');
  }

  /** Freitextsuche über Name/Beschreibung der myChecks. */
  searchQuery = '';

  /**
   * Stand des myCheck-Vorlagenlimits; `null` = noch nicht geladen oder nicht lesbar.
   *
   * <p>Nicht lesbar wird bewusst wie "keine Grenze" behandelt: sonst sperrt ein Fehler im
   * Quota-Endpunkt das Anlegen komplett. Das Backend lehnt im Zweifel selbst mit 402 ab.
   */
  templateQuota: MyCheckQuota | null = null;

  /** Vorlagenkontingent erschöpft – Anlegen und Quickstart sind dann gesperrt. */
  get templateQuotaExhausted(): boolean {
    return this.templateQuota?.remaining != null && this.templateQuota.remaining <= 0;
  }

  /** Tooltip/Hinweis für die gesperrten Anlegen-Wege. */
  get templateQuotaTooltip(): string {
    const limit = this.templateQuota?.limit;
    return `Ihr Abo erlaubt ${limit ?? 0} myCheck-Vorlagen und ist ausgeschöpft.`
      + ' Löschen Sie eine bestehende Vorlage oder erweitern Sie Ihr Abo im Shop.';
  }

  /** Darf ein neuer myCheck angelegt werden? Recht UND Kontingent. */
  get canCreateRuleset(): boolean {
    return this.canManageRules && !this.templateQuotaExhausted;
  }

  get canDeleteRules(): boolean {
    // „myCheck löschen" – einheitlich für alle drei Arten, wie im Backend.
    return this.permissionService.hasProjectPrivilege('PROJECT_MYCHECK_DELETE');
  }

  protected readonly noManageRulesTooltip = 'Sie haben nicht die erforderlichen Berechtigungen, um myChecks zu verwalten.';
  protected readonly noDeleteRulesTooltip = 'Sie haben nicht die erforderlichen Berechtigungen, um Prüfregeln zu löschen.';

  /** Regelwerk-Admin (darf bereitgestellte globale Regelwerke bearbeiten). */
  private get isRulesetAdmin(): boolean {
    return this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN');
  }

  onDuplicateRequested(ruleset: Ruleset): void {
    // Bewusst ohne isLocked(): read-only (bereitgestellt oder aus einem anderen Projekt geteilt)
    // heißt „nicht bearbeiten" – Duplizieren ist dort die einzige Hauptaktion der Karte.
    if (!this.canManageRules) return;
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) {
      return;
    }
    if (this.templateQuotaExhausted) {
      this.toastService.warning(this.templateQuotaTooltip);
      return;
    }

    this.rulesetService.duplicateRuleset(ruleset, projectId, `${ruleset.name} (Kopie)`).subscribe({
      next: () => this.loadRulesets(),
      error: err => {
        console.error('Ruleset duplizieren fehlgeschlagen:', err);
        this.reportCreationError(err);
      },
    });
  }

  /**
   * Meldet einen fehlgeschlagenen Anlege-/Kopiervorgang.
   *
   * <p>402 wird gesondert benannt: das ist das erschöpfte Vorlagenkontingent, und die Meldung des
   * Backends nennt Limit und Verbrauch. Vorher landete das nur in der Konsole und der Nutzer sah
   * gar nichts.
   */
  private reportCreationError(err: any): void {
    if (err?.status === 402) {
      this.toastService.warning(err?.error?.message ?? this.templateQuotaTooltip);
      this.loadTemplateQuota();
      return;
    }
    this.toastService.error('myCheck konnte nicht angelegt werden.');
  }

  /**
   * Bewusst nicht in der Liste: bereitgestellte myChecks kommen über das Duplizieren eines
   * Katalog-Prüflaufs ins Projekt. Import-Rückmeldung und Löschen brauchen sie trotzdem.
   */
  prebuiltRulesets: Ruleset[] = [];
  customRulesets: Ruleset[] = [];
  loading = true;
  /** Prüfläufe, die das zu löschende myCheck verwenden (Anzeige im Lösch-Popup) */
  deleteUsages: MyCheckUsage[] = [];
  /** Aktive Filter aus der BIC-Filter-Komponente (Key 'type' = Prüfregeltyp). */
  activeFilters: { [key: string]: any[] } = {};
  filterConfig: BicFilterConfig[] = [
    {
      key: 'type',
      displayName: 'Prüfregeltyp',
      type: 'status',
      options: ['KI', 'HYBRID', 'IDS'] as RulesetType[],
      formatOption: (value: RulesetType) => RULESET_TYPE_CONFIG[value]?.label ?? value,
    },
  ];

  /** Popup state: 'none' | 'prompt' */
  popupState: 'none' | 'prompt' = 'none';

  /** Ruleset pending deletion (null = no delete popup) */
  rulesetToDelete: Ruleset | null = null;
  /** Regelset, für das der Direktstart gerade vorbereitet wird (Button-Ladezustand). */
  preparingRunRulesetId: string | null = null;
  deleteUsagesLoading = false;

  // ─── Export / Import (.bicrs) ───────────────────────────────────────────────

  /** Dateiendung für das `accept`-Attribut des Datei-Dialogs. */
  protected readonly packageExtension = RULESET_PACKAGE_EXTENSION;
  /** id des Regelsets, dessen Paket gerade erzeugt wird (Mehrfachklick verhindern). */
  protected exportingRulesetId: string | null = null;

  /** „myCheck löschen" (früher „Prüfregeln löschen"). */
  /**
   * Prüfungen ausführen. Bewusst NICHT das Verwalten-Recht: der Direktstart eines myChecks ist
   * eine Ausführung, und der dabei automatisch entstehende unsichtbare Prüflauf ist ein
   * Implementierungsdetail, das kein zusätzliches Recht rechtfertigt.
   */
  get canExecuteChecks(): boolean {
    return this.permissionService.hasProjectPrivilege('PROJECT_CHECK_EXECUTE');
  }
  protected importing = false;
  /** Ergebnis des letzten Imports – wird im Ergebnis-Dialog angezeigt. */
  protected importResult: RulesetImportResult | null = null;
  /** Fehlermeldungen des letzten Imports; leer = kein Fehler. */
  protected importErrors: string[] = [];

  protected readonly faPlus = faPlus;
  protected readonly faMagnifyingGlass = faMagnifyingGlass;
  /** Läuft der Quickstart, ersetzt der eingebettete Chat die Liste. */
  quickstartActive = false;
  private quickstartCreatedSubscription?: Subscription;
  protected readonly faStar = faStar;
  showFavoritesPopup = false;
  /** myCheck, für den gerade ein Prüflauf angelegt wird (null = Dialog geschlossen). */
  createCheckRunFor: Ruleset | null = null;
  creatingCheckRun = false;
  protected readonly faClipboardList = faClipboardList;
  protected readonly faArrowDownWideShort = faArrowDownWideShort;
  protected readonly faArrowUpWideShort = faArrowUpWideShort;
  protected readonly faCircleInfo = faCircleInfo;
  quickstartInitializing = false;
  /** Name des myChecks für die Topbar („myCheck - <Name>"). */
  quickstartRulesetName = '';
  protected readonly faSpinnerThird = faSpinnerThird;
  private quickstartCompletedSubscription?: Subscription;
  private quickstartSeriesSubscription?: Subscription;
  private createRequestSubscription?: Subscription;

  ngOnInit(): void {
    this.loadRulesets();
    this.loadCatalogState();
    this.resumeQuickstartIfRequested();
    // Der Quickstart läuft im Floating-Chat auf dieser Seite. Ist er fertig, zeigt die Karte des
    // myChecks „Vollständig" – dafür muss die Liste neu geladen werden.
    this.quickstartCompletedSubscription = this.chatService.mycheckRulesetCompleted.subscribe(rulesetId => {
      this.onQuickstartCompleted(rulesetId);
    });
    // Sobald der Agent den myCheck angelegt hat, erscheint er in der Liste – das Gespräch läuft
    // aber weiter (Regeln, Prompts, Ausgabe folgen noch).
    this.quickstartCreatedSubscription = this.chatService.mycheckRulesetCreated.subscribe(created => {
      // Der Platzhalter in der URL ist ab jetzt ungültig: ein Reload suchte sonst nach einer
      // Session, die es unter diesem Schlüssel nicht mehr gibt.
      if (this.quickstartActive) {
        this.writeQuickstartParam(created.rulesetId);
      }
      this.loadRulesets();
    });
    // Mehrere myChecks in einem Quickstart (BIC-Backend#189): der Chat bleibt offen, bis die Serie durch
    // ist. Jeder erledigte Punkt aktualisiert die Liste; am Ende meldet ein Hinweis den Prüflauf.
    this.quickstartSeriesSubscription = this.chatService.mycheckSeriesChanged.subscribe(series => {
      if (!series || !this.quickstartActive) return;
      this.loadRulesets();
      if (series.checkRunDefinitionId && series.checkRunDefinitionId !== this.announcedCheckRunId) {
        this.announcedCheckRunId = series.checkRunDefinitionId;
        this.toastService.success('Prüflauf aus den myChecks der Serie angelegt');
      }
    });
    // `?create=quickstart`: der Anlegen-Link aus chatBIC (`McpLinks.createMyCheck`). Abonniert statt
    // aus dem Snapshot gelesen, weil der Link auch kommt, während der Nutzer schon auf dieser Seite
    // steht. Geöffnet wird derselbe Dialog wie über den Knopf – Name und Assistent wählt der Nutzer.
    this.createRequestSubscription = this.route.queryParamMap.subscribe(params => {
      if (params.get('create') !== 'quickstart') return;
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {create: null},
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      this.openCreatePopup();
    });
  }

  ngOnDestroy(): void {
    this.quickstartCompletedSubscription?.unsubscribe();
    this.quickstartCreatedSubscription?.unsubscribe();
    this.quickstartSeriesSubscription?.unsubscribe();
    this.createRequestSubscription?.unsubscribe();
    if (this.chatService.isMycheckSurfaceActive) {
      this.chatService.closeMycheckSurface();
    }
  }

  onExportRequested(ruleset: Ruleset): void {
    // Defensive: vorgefertigte (BIC-bereitgestellte) myChecks sind nicht exportierbar –
    // die UI blendet die Aktion bereits aus, hier zusätzlich abgesichert.
    if (!this.canManageRules || ruleset.isPrebuilt || this.exportingRulesetId) return;
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) return;

    this.exportingRulesetId = ruleset.id;
    this.rulesetService.exportRuleset(ruleset.id, projectId).subscribe({
      next: ({blob, fileName}) => {
        this.rulesetService.triggerDownload(blob, fileName);
        this.exportingRulesetId = null;
      },
      error: err => {
        this.exportingRulesetId = null;
        // Der Fehlertext steckt bei responseType 'blob' im Blob, nicht im Objekt – deshalb
        // wird er hier ausgelesen, statt eine unspezifische Meldung zu zeigen.
        this.readBlobError(err).then(messages => {
          this.importResult = null;
          this.importErrors = messages.length > 0
            ? messages
            : ['Der myCheck konnte nicht exportiert werden.'];
        });
      },
    });
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Zurücksetzen, damit dieselbe Datei erneut gewählt werden kann (change feuert sonst nicht).
    input.value = '';
    if (!file || !this.canManageRules) return;

    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) return;

    this.importing = true;
    this.importResult = null;
    this.importErrors = [];
    this.rulesetService.importRuleset(file, projectId).subscribe({
      next: result => {
        this.importing = false;
        this.importResult = result;
        this.loadRulesets();
      },
      error: err => {
        this.importing = false;
        const response = err?.error;
        const messages: string[] = [];
        if (response?.message) messages.push(response.message);
        // `details` trägt bei einer Validierungsantwort die einzelnen Gründe. Der erste Eintrag
        // wiederholt oft die message – Duplikate werden deshalb übersprungen.
        for (const detail of response?.details ?? []) {
          if (!messages.includes(detail)) messages.push(detail);
        }
        this.importErrors = messages.length > 0
          ? messages
          : ['Die Datei konnte nicht importiert werden.'];
      },
    });
  }

  closeImportFeedback(): void {
    this.importResult = null;
    this.importErrors = [];
  }

  /** Öffnet das importierte Regelset direkt im passenden Editor. */
  openImportedRuleset(): void {
    const result = this.importResult;
    if (!result) return;
    this.closeImportFeedback();
    const all = [...this.prebuiltRulesets, ...this.customRulesets];
    const imported = all.find(r => r.id === result.rulesetId);
    if (imported) {
      this.navigateToEditor(imported);
    }
  }

  /** Liest eine Fehlerantwort aus, die wegen `responseType: 'blob'` als Blob ankommt. */
  private async readBlobError(err: any): Promise<string[]> {
    const body = err?.error;
    if (!(body instanceof Blob)) {
      return body?.message ? [body.message] : [];
    }
    try {
      const parsed = JSON.parse(await body.text());
      const messages: string[] = [];
      if (parsed?.message) messages.push(parsed.message);
      for (const detail of parsed?.details ?? []) {
        if (!messages.includes(detail)) messages.push(detail);
      }
      return messages;
    } catch {
      return [];
    }
  }

  /** Die im Tab "Eigene" gezeigten myChecks. */
  get activeRulesets(): Ruleset[] {
    return this.applyFilters(this.customRulesets);
  }

  /** Text für den Leerzustand – unterscheidet "wirklich keine vorhanden" von "durch Suche/Filter herausgefiltert". */
  get emptyStateMessage(): string {
    const hasQuery = this.searchQuery.trim().length > 0 || (this.activeFilters['type']?.length ?? 0) > 0;
    return hasQuery ? 'Keine eigenen myChecks gefunden.' : 'Noch keine eigenen myChecks vorhanden.';
  }

  onFiltersChange(filters: { [key: string]: any[] }): void {
    this.activeFilters = filters;
  }

  /** Aktive Sortierung – wirkt auf beide Bereiche (Eigene/BIC) gleich. */
  sortField: RulesetSortField = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';
  protected readonly sortFieldOptions = RULESET_SORT_FIELD_OPTIONS;

  /** Setzt Sortierfeld und -richtung direkt (je ein Pfeil-Button pro Feld im Dropdown). */
  applySort(field: RulesetSortField, direction: 'asc' | 'desc'): void {
    this.sortField = field;
    this.sortDirection = direction;
  }

  private applyFilters(rulesets: Ruleset[]): Ruleset[] {
    let result = rulesets;

    const selected = this.activeFilters['type'] ?? [];
    if (selected.length > 0) {
      result = result.filter(r => selected.includes(r.type));
    }

    const q = this.searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(r =>
        r.name.toLowerCase().includes(q) || (r.description?.toLowerCase().includes(q) ?? false),
      );
    }

    return this.sortRulesets(result);
  }

  private sortRulesets(rulesets: Ruleset[]): Ruleset[] {
    const sorted = [...rulesets];
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    switch (this.sortField) {
      case 'status':
        sorted.sort((a, b) => (rulesetStatusRank(a.status) - rulesetStatusRank(b.status)) * dir
          || a.name.localeCompare(b.name, 'de'));
        break;
      case 'updated':
        sorted.sort((a, b) => (dateValue(a.updatedAt ?? a.createdAt) - dateValue(b.updatedAt ?? b.createdAt)) * dir);
        break;
      default:
        sorted.sort((a, b) => a.name.localeCompare(b.name, 'de') * dir);
    }
    return sorted;
  }

  onPromptCreated(request: RulesetCreationRequest): void {
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) return;

    const data = request.ruleset;
    this.popupState = 'none';

    if (request.startInQuickstartMode) {
      // Bewusst NICHTS anlegen: welcher Typ entsteht (KI für Dokumente, IDS für das IFC-Modell),
      // entscheidet der Chat-Agent im Interview – und die beiden landen in verschiedenen Tabellen.
      // Ein hier vorab erzeugtes KI-Set wäre bei einer IDS-Entscheidung eine leere Leiche.
      void this.startPendingQuickstart(data.name || 'Neuer myCheck');
      return;
    }

    const type = data.type || 'KI';

    if (type === 'KI') {
      // Create KI ruleset via eigenständige Prüfregel-API
      const regelSet = {
        name: data.name || 'Neuer myCheck',
        description: data.description || '',
        type: 'KI' as const,
        prebuilt: false,
        projectId,
        rules: [],
      };
      this.rulesetService.createKiRuleset(regelSet).subscribe({
        next: saved => {
          const newRuleset: Ruleset = {
            id: saved.id ?? '',
            name: saved.name ?? data.name ?? 'myCheck',
            description: saved.description ?? data.description ?? '',
            type: 'KI',
            status: 'DRAFT',
            ruleCount: 0,
            allowedFileTypes: data.allowedFileTypes || ['.pdf'],
            checkMode: data.checkMode || 'SINGLE',
            isPrebuilt: false,
          };
          this.customRulesets = [...this.customRulesets, newRuleset];
          this.navigateToEditor(newRuleset);
        },
        error: err => {
          console.error('KI-Ruleset erstellen fehlgeschlagen:', err);
          this.reportCreationError(err);
        },
      });
    } else if (type === 'IDS') {
      // Navigate to IDS editor – rule will be created on first save
      this.router.navigate([
        'projects', projectId, 'my-checks', 'new', 'ids'
      ], {state: {ruleName: data.name || '', ruleDescription: data.description || ''}});
    } else if (type === 'HYBRID') {
      // Create Hybrid ruleset via Workflow Definitions API
      const manifest = {
        name: data.name || 'Neuer hybrider myCheck',
        version: 'v1',
        description: data.description || '',
        rulesetType: 'HYBRID',
        trigger: {
          nodeId: 'trigger_0',
          moduleId: 'MANUAL_TRIGGER',
          config: {},
        },
        steps: [],
      };
      this.rulesetService.saveHybridRuleset(manifest, projectId).subscribe({
        next: def => {
          const newRuleset: Ruleset = {
            id: def.id ?? '',
            name: def.name ?? data.name ?? 'Hybrider myCheck',
            description: data.description ?? '',
            type: 'HYBRID',
            status: 'DRAFT',
            ruleCount: 0,
            allowedFileTypes: data.allowedFileTypes || ['.ifc'],
            checkMode: data.checkMode || 'SINGLE',
            isPrebuilt: false,
          };
          this.customRulesets = [...this.customRulesets, newRuleset];
          // Von Hand angelegt heißt: direkt in den Hybrid-Editor. Über den Quickstart entstehen
          // hybride myChecks seit 09/2026 ebenfalls (Create-Hybrid-myCheck) – dieser Pfad hier ist
          // der manuelle daneben, nicht der einzige.
          this.navigateToEditor(newRuleset);
        },
        error: err => console.error('Hybrid-Ruleset erstellen fehlgeschlagen:', err),
      });
    }
  }

  openCreatePopup(): void {
    if (!this.canManageRules) return;
    if (this.templateQuotaExhausted) {
      this.toastService.error(this.templateQuotaTooltip);
      return;
    }
    this.popupState = 'prompt';
  }

  onPromptClosed(): void {
    this.popupState = 'none';
  }

  onOpenRuleset(ruleset: Ruleset): void {
    // Zentral bereitgestellte Sets sind ohne Regelwerk-Admin-Recht nicht einmal zum Ansehen
    // öffenbar; eigene/andere Sets erfordern "myCheck verwalten". Der "Öffnen"-Button ist
    // dafür in der Karte bereits deaktiviert; hier zusätzlich als Absicherung gegen direkte Aufrufe.
    if (this.isLocked(ruleset) || !this.canManageRules) return;
    this.navigateToEditor(ruleset);
  }

  /**
   * Direktstart: den myCheck ausführen, ohne vorher einen Prüflauf zu bauen.
   *
   * <p>Der Prüflauf dazu wird automatisch (und für den Nutzer unsichtbar) mit den
   * Default-Prüflaufeinstellungen angelegt bzw. aktualisiert; danach übernimmt der ganz normale
   * Ausführungspfad. Für global bereitgestellte Sets bewusst erlaubt – der Lauf findet im eigenen
   * Projekt statt (nur Öffnen/Bearbeiten bleibt dort gesperrt, siehe {@link onOpenRuleset}).
   */
  onExecuteRuleset(ruleset: Ruleset): void {
    // Absicherung gegen direkte Aufrufe; die Karte deaktiviert den Button bereits.
    if (!this.canExecuteChecks || this.preparingRunRulesetId) return;
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) return;

    this.preparingRunRulesetId = ruleset.id;
    this.rulesetService.prepareDirectRun(ruleset, projectId).subscribe({
      next: definitionId => {
        this.preparingRunRulesetId = null;
        this.router.navigate(['projects', projectId, 'check-runs', 'execute'], {
          queryParams: {definitionId},
        });
      },
      error: () => {
        // Kein stiller Fallback: ohne Prüflauf-Definition gibt es nichts auszuführen.
        this.preparingRunRulesetId = null;
        this.toastService.error('Die Prüfung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.');
      },
    });
  }

  onDeleteRequested(ruleset: Ruleset): void {
    if (!this.canDeleteRules || this.isLocked(ruleset)) return;
    // Mit anderen Projekten geteilt: statt der einfachen Rückfrage die Wahl zwischen Besitz
    // übergeben und aus allen Projekten löschen.
    if ((ruleset.sharedProjects?.length ?? 0) > 1 && !ruleset.linkedIn) {
      this.ownershipMode = 'delete';
      this.ownershipRuleset = ruleset;
      return;
    }
    this.rulesetToDelete = ruleset;
    this.loadDeleteUsages(ruleset);
  }

  /**
   * Verwendung nachladen, damit im Popup steht, welche Prüfläufe das Löschen mitbekommen.
   *
   * <p>Stand vorher als unerreichbarer Code hinter dem {@code return} in {@link isLocked} – das Popup
   * hat die Verwendungen deshalb nie angezeigt.
   */
  private loadDeleteUsages(ruleset: Ruleset): void {
    this.deleteUsages = [];
    this.deleteUsagesLoading = true;
    this.rulesetService.findMyCheckUsages([ruleset.id], this.stateConfigService.selectedProject?.id).subscribe({
      next: usages => {
        // Nur übernehmen, wenn das Popup noch zu diesem Regelset gehört (Nutzer kann zwischenzeitlich
        // abgebrochen und ein anderes Set gewählt haben).
        if (this.rulesetToDelete?.id !== ruleset.id) return;
        this.deleteUsages = usages;
        this.deleteUsagesLoading = false;
      },
      error: () => {
        if (this.rulesetToDelete?.id !== ruleset.id) return;
        this.deleteUsagesLoading = false;
      },
    });
  }

  /** Der zuletzt gemeldete Prüflauf einer Serie – gegen einen zweiten Hinweis bei erneutem Signal. */
  private announcedCheckRunId: string | null = null;

  /** Läuft gerade das Anlegen hinter „Übernehmen"? Sperrt den Button gegen Doppelklicks. */
  protected applyingQuickstart = false;

  /**
   * Topbar „Übernehmen": den besprochenen myCheck speichern und zurück zur Liste.
   *
   * <p>Vorher verließ der Button nur den Quickstart. Das ging davon aus, dass der Agent schon
   * angelegt hatte – nach der Zusammenfassung wartet er aber noch auf die Bestätigung, und der
   * Klick verwarf das ganze Gespräch, ohne dass je ein myCheck entstand. Existiert er noch nicht,
   * wird er deshalb zuerst über den Agenten angelegt.
   */
  async applyQuickstart(): Promise<void> {
    if (this.chatService.activeMycheckRulesetId) {
      this.leaveQuickstart();
      return;
    }
    if (this.applyingQuickstart) return;
    if (this.chatService.session('mycheck').isBotThinking) {
      this.toastService.info('chatBIC arbeitet noch – bitte warte die Antwort ab.');
      return;
    }

    this.applyingQuickstart = true;
    // Vor dem Senden abonnieren: das Ende des Turns darf nicht zwischen Senden und Warten fallen.
    const turnDone = firstValueFrom(this.chatService.turnFinished.pipe(filter(surface => surface === 'mycheck')));
    try {
      await this.chatService.requestMycheckCreation();
      await turnDone;
    } catch (error) {
      console.error('Übernehmen im Quickstart fehlgeschlagen:', error);
    } finally {
      this.applyingQuickstart = false;
    }

    // Das Abschluss-Signal kann den Quickstart bereits beendet haben (onQuickstartCompleted).
    if (!this.quickstartActive) return;
    if (this.chatService.activeMycheckRulesetId) {
      this.leaveQuickstart();
      this.toastService.success('myCheck wurde gespeichert');
    } else {
      this.toastService.error('Der myCheck konnte noch nicht angelegt werden – die Antwort von chatBIC steht im Chat.');
    }
  }

  /**
   * Quickstart verlassen und zur Liste zurück – der myCheck bleibt wie er ist.
   *
   * @param fallbackKey Schlüssel aus dem Abschluss-Signal; greift, wenn der Dienst seinen
   *                    In-Memory-Zustand schon zurückgesetzt hat.
   */
  leaveQuickstart(fallbackKey?: string): void {
    const mycheckKey = this.chatService.activeMycheckKey ?? fallbackKey;
    this.quickstartActive = false;
    this.quickstartInitializing = false;
    this.quickstartRulesetName = '';
    this.chatService.closeMycheckSurface();
    if (mycheckKey) {
      // Auch ein Platzhalter muss weg, sonst nimmt die Wiederaufnahme einen Chat wieder auf,
      // zu dem es keinen myCheck gibt.
      this.chatService.forgetMycheckQuickstart(mycheckKey);
    }
    this.writeQuickstartParam(null);
    this.loadRulesets();
  }

  /**
   * Startet den Quickstart, bevor es den myCheck gibt.
   *
   * <p>Der Typ (und damit die Tabelle) steht erst nach dem Interview fest, deshalb läuft der Chat
   * bis dahin auf einem Platzhalter-Schlüssel. Sobald der Agent angelegt hat, meldet er ID und Typ
   * über `ruleset_created` und {@link onQuickstartRulesetCreated} übernimmt sie.
   */
  private async startPendingQuickstart(checkName: string): Promise<void> {
    // Der Quickstart legt den myCheck erst am Ende des Interviews an (über die MCP-Tools). Ohne
    // diese Sperre führte der Nutzer ein komplettes Gespräch und liefe erst dann in den 402.
    if (this.templateQuotaExhausted) {
      this.toastService.error(this.templateQuotaTooltip);
      return;
    }
    const pendingKey = this.chatService.startPendingMycheckQuickstart();
    this.chatService.queueMycheckBootstrapForNewCheck(pendingKey, checkName);
    await this.startQuickstart(pendingKey, checkName, true);
  }

  /**
   * Gemeinsamer Einstieg: eingebetteter Chat, Aktiv-Merker, Thread – für beide Startarten.
   *
   * @param mycheckKey echte myCheck-ID oder Platzhalter (siehe `startPendingMycheckQuickstart`)
   * @param isPending true, solange der myCheck noch nicht existiert
   */
  private async startQuickstart(mycheckKey: string, name: string, isPending: boolean): Promise<void> {
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId || !mycheckKey) return;

    this.chatService.openMycheckSurface();
    this.quickstartRulesetName = (name ?? '').trim();
    this.chatService.rememberMycheckQuickstartActive(mycheckKey, this.quickstartRulesetName);
    // Eingebetteter Chat, nicht die Floating-Sidebar – wie im bisherigen Quickstart.
    this.chatService.isFloatingMode = false;
    this.chatService.floatingChatOpen = false;
    this.quickstartActive = true;
    this.quickstartInitializing = true;
    // Die Session bekommt ihren Schlüssel in die URL: ein Reload findet genau diesen Chat wieder,
    // und ein Aufruf von `/my-checks` ohne den Parameter bleibt in der Übersicht.
    this.writeQuickstartParam(mycheckKey);

    const projectChanged = this.chatService.currentProject !== projectId;
    this.chatService.currentProject = projectId;
    if (projectChanged) {
      this.chatService.initializeChat();
    }

    await this.startQuickstartThread(mycheckKey, isPending);
  }

  /**
   * Wartet, bis der ACS-Client bereit ist, und stellt Thread plus Startnachricht her.
   *
   * <p>Der Client wird asynchron initialisiert; ohne das Warten liefe `ensureMycheckThread` gegen
   * einen noch nicht existierenden Client.
   */
  private async startQuickstartThread(mycheckKey: string, isPending: boolean, retries = 10): Promise<void> {
    if (!this.chatService.isChatReady()) {
      if (retries <= 0) {
        this.quickstartInitializing = false;
        this.toastService.error('myCheck Quickstart konnte nicht gestartet werden');
        return;
      }
      window.setTimeout(() => void this.startQuickstartThread(mycheckKey, isPending, retries - 1), 300);
      return;
    }

    try {
      await this.chatService.ensureMycheckThread(mycheckKey, isPending);
      await this.chatService.flushQueuedMycheckBootstrap(mycheckKey);
    } catch {
      this.toastService.error('myCheck Quickstart konnte nicht gestartet werden');
    } finally {
      this.quickstartInitializing = false;
    }
  }

  /**
   * Nimmt einen Quickstart wieder auf – aber nur den, den die URL ausdrücklich nennt.
   *
   * <p>Der Aktiv-Merker im sessionStorage allein genügt nicht: er überlebt auch Sessions, die
   * anderswo beendet wurden, und `/my-checks` öffnete dann statt der Übersicht deren Chat. Mit dem
   * Schlüssel in der URL (`?quickstart=<myCheck-/Platzhalter-ID>`) bleibt der Reload-Fall erhalten,
   * während jeder gewöhnliche Aufruf der Seite in der Liste landet.
   */
  private resumeQuickstartIfRequested(): void {
    const requestedKey = this.route.snapshot.queryParamMap.get(RuleManagementOverviewComponent.QUICKSTART_PARAM);
    if (!requestedKey) return;

    const active = this.chatService.getActiveMycheckQuickstart();
    if (!active || active.rulesetId !== requestedKey) {
      // Session abgeschlossen oder gehört zu einem anderen Projekt: der Parameter zeigt ins Leere
      // und würde beim nächsten Reload erneut einen leeren Chat öffnen.
      this.writeQuickstartParam(null);
      return;
    }

    void this.resumeQuickstart(active);
  }

  /**
   * Nimmt die adressierte Session auf – aber nur, wenn ihr Chat noch existiert.
   *
   * <p>Ohne die Prüfung legte `ensureMycheckThread` für einen verschwundenen Thread einen neuen,
   * leeren an: der Nutzer sähe einen leeren Quickstart statt seines Gesprächs.
   */
  private async resumeQuickstart(active: { rulesetId: string; name: string }): Promise<void> {
    let resumable: boolean;
    try {
      resumable = await this.chatService.canResumeMycheckQuickstart(active.rulesetId);
    } catch {
      // Unklar (z.B. Drosselung durch ACS) ist nicht dasselbe wie "weg": die Session bleibt
      // gemerkt, der Nutzer bleibt in der Übersicht und kann es erneut versuchen.
      this.toastService.warning('Der laufende myCheck Quickstart konnte gerade nicht geladen werden.');
      return;
    }

    if (!resumable) {
      this.chatService.forgetMycheckQuickstart(active.rulesetId);
      this.writeQuickstartParam(null);
      return;
    }

    // Der gemerkte Schlüssel kann ein Platzhalter sein: dann war das Interview beim Reload noch
    // vor der Typentscheidung, der myCheck existiert also nicht. Als echte ID weitergegeben
    // landete der Platzhalter in der Bot-Metadata, und der Agent hielte ihn für ein bestehendes
    // myCheck – er bekäme nur Edit-Tools und könnte nichts mehr anlegen.
    const isPending = ChatService.isPendingMycheckKey(active.rulesetId);
    if (!isPending) {
      this.chatService.activeMycheckType = this.chatService.getRememberedMycheckType(active.rulesetId);
    }
    await this.startQuickstart(active.rulesetId, active.name, isPending);
  }

  /**
   * Schreibt den Schlüssel der laufenden Quickstart-Session in die URL bzw. entfernt ihn.
   *
   * <p>`replaceUrl`, weil der Quickstart kein eigener Navigationsschritt ist: sonst müsste der
   * Nutzer mehrfach zurück, um die Seite zu verlassen.
   */
  private writeQuickstartParam(key: string | null): void {
    const param = RuleManagementOverviewComponent.QUICKSTART_PARAM;
    const current = this.route.snapshot.queryParamMap.get(param);
    if ((current ?? null) === key) return;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {[param]: key},
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private onQuickstartCompleted(rulesetId?: string): void {
    this.leaveQuickstart(rulesetId);
    this.toastService.success('myCheck ist fertig und kann gestartet werden');
  }

  private loadRulesets(): void {
    this.loading = true;
    const projectId = this.stateConfigService.selectedProject?.id;
    this.rulesetService.listRulesets(projectId).subscribe(result => {
      this.prebuiltRulesets = result.prebuilt;
      this.customRulesets = result.custom;
      this.loading = false;
    });
    this.loadTemplateQuota();
  }

  /**
   * Holt den Stand des Vorlagenlimits. Wird bei jedem Neuladen der Liste mitgeholt – Anlegen,
   * Duplizieren, Import und Löschen verändern ihn alle.
   */
  private loadTemplateQuota(): void {
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) {
      this.templateQuota = null;
      return;
    }
    this.rulesetService.getQuota(projectId).subscribe({
      next: quota => this.templateQuota = quota,
      // Kein Toast: der Stand ist Komfort, nicht Funktion. Bleibt null = nicht gesperrt.
      error: () => this.templateQuota = null,
    });
  }

  onCreateCheckRunRequested(ruleset: Ruleset): void {
    // Absicherung gegen direkte Aufrufe; die Karte blendet die Aktion bereits aus.
    if (!this.canManageMyChecks || this.creatingCheckRun) return;
    this.createCheckRunFor = ruleset;
  }

  closeCreateCheckRun(): void {
    if (this.creatingCheckRun) return;
    this.createCheckRunFor = null;
  }

  /** Legt den Prüflauf an und öffnet ihn im Builder – Dateiauswahl und Bericht fehlen noch. */
  onCreateCheckRunConfirmed(name: string): void {
    const ruleset = this.createCheckRunFor;
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!ruleset || !projectId || this.creatingCheckRun) return;

    this.creatingCheckRun = true;
    this.rulesetService.createCheckRunForRuleset(ruleset, projectId, name).subscribe({
      next: definition => {
        this.creatingCheckRun = false;
        this.createCheckRunFor = null;
        if (!definition?.id) {
          this.toastService.error('Prüflauf konnte nicht erstellt werden');
          return;
        }
        void this.router.navigate(['projects', projectId, 'check-runs', 'builder', definition.id]);
      },
      error: () => {
        this.creatingCheckRun = false;
        this.toastService.error('Prüflauf konnte nicht erstellt werden');
      },
    });
  }

  openFavoritesPopup(): void {
    this.showFavoritesPopup = true;
  }

  onFavoritesClosed(): void {
    this.showFavoritesPopup = false;
  }

  // ─── Delete ─────────────────────────────────────────────────────────────────

  /** Einreichung ist durch – Dialog schließen und das Badge der Karte sofort aktualisieren. */
  onCatalogSubmitted(submissions: CatalogSubmission[]): void {
    this.provideTarget = null;
    const next = new Map(this.catalogSubmissions);
    for (const submission of submissions) {
      rememberLatestSubmission(next, submission);
    }
    this.catalogSubmissions = next;
  }

  /** Letzte Katalog-Einreichung eines Regelsets (für das Karten-Badge). */
  catalogSubmissionFor(ruleset: Ruleset): CatalogSubmission | null {
    // Enthaltener myCheck: seine Einreichung ist eine des Prüflaufs, zugeordnet über componentSourceId.
    return this.catalogSubmissions.get(catalogSourceKey(ruleset.type, ruleset.id))
      ?? this.catalogSubmissions.get(catalogComponentKey(ruleset.id))
      ?? null;
  }

  /**
   * Katalog-Zustand für Admins: Einreichen erlaubt? + eigene Einreichungen (einmal, ohne Filter,
   * statt pro Karte). Für Nicht-Admins gibt es weder Menüeintrag noch Badge – die Endpunkte
   * verlangen GLOBAL_CHECKRULE_ADMIN, ein Aufruf wäre ein sicherer 403.
   */
  private loadCatalogState(): void {
    if (!this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN')) return;
    this.catalogService.getConfig().subscribe({
      next: config => (this.catalogSubmissionAllowed = config.submissionAllowed),
      error: err => this.toastService.error(
        serverErrorMessage(err, 'Katalog-Konfiguration konnte nicht geladen werden.')),
    });
    this.catalogService.listSubmissions().subscribe({
      next: submissions => (this.catalogSubmissions = latestSubmissionBySource(submissions)),
      error: err => this.toastService.error(
        serverErrorMessage(err, 'Katalog-Einreichungen konnten nicht geladen werden.')),
    });
  }

  cancelDelete(): void {
    this.rulesetToDelete = null;
    this.deleteUsages = [];
    this.deleteUsagesLoading = false;
  }

  confirmDelete(): void {
    if (!this.rulesetToDelete) return;
    const ruleset = this.rulesetToDelete;
    const projectId = this.stateConfigService.selectedProject?.id;
    this.rulesetService.deleteRuleset(ruleset, projectId).subscribe({
      next: () => {
        this.prebuiltRulesets = this.prebuiltRulesets.filter(r => r.id !== ruleset.id);
        this.customRulesets = this.customRulesets.filter(r => r.id !== ruleset.id);
        this.rulesetToDelete = null;
      },
      error: err => {
        console.error('Fehler beim Löschen des Regelsets:', err);
        // Z. B. „mit anderen Projekten geteilt" – die Meldung des Backends sagt, was zu tun ist.
        this.toastService.error(serverErrorMessage(err, 'myCheck konnte nicht gelöscht werden.'));
        this.rulesetToDelete = null;
      },
    });
  }

  /** Read-only für den aktuellen Nutzer: bereitgestelltes Regelwerk ohne Admin-Recht. */
  private isLocked(ruleset: Ruleset): boolean {
    return !!ruleset.readOnly && !this.isRulesetAdmin;
  }

  // ─── Zentral bereitstellen (Adminfunktion) ──────────────────────────────────
  private catalogService = inject(CatalogService);
  private toastService = inject(BicToastService);

  /**
   * Öffnet den Bereitstellen-Dialog. Die Rechteprüfung passiert zusätzlich hier, nicht nur auf der
   * Karte – ein Direktaufruf soll nicht durchrutschen.
   */
  onProvideGloballyRequested(ruleset: Ruleset): void {
    if (!this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN')) return;
    if (!this.catalogSubmissionAllowed) return;
    this.provideTarget = ruleset;
  }

  closeProvideDialog(): void {
    this.provideTarget = null;
  }

  private navigateToEditor(ruleset: Ruleset): void {
    const projectId = this.stateConfigService.selectedProject.id;
    const typeMap: Record<string, string> = {KI: 'ki', HYBRID: 'hybrid', IDS: 'ids'};
    const typePath = typeMap[ruleset.type] || 'ki';
    // Bereitgestellte Regelwerke ohne Admin-Recht nur read-only öffnen. Konvention `readOnly=true`
    // (vom workflow-editor bereits unterstützt) statt eines eigenen mode-Params.
    const queryParams = this.isLocked(ruleset) ? {readOnly: 'true'} : {};
    this.router.navigate(['projects', projectId, 'my-checks', ruleset.id, typePath], {queryParams});
  }

}
